import { useState, useEffect } from "react";
import { Users, Camera, Mic, MapPin, AlertTriangle, CheckCircle, Clock, Bug, Leaf, TrendingUp, Plus, Shield, Star, MessageSquare, Cloud, ThumbsUp, Navigation, X, Eye, Info } from "lucide-react";
import { supabase } from "../lib/supabaseClient.ts";
import { testCommunityReportsTable, testStorageBuckets } from "../utils/testDatabase.ts";
import { alertService, type LocalAlert } from "../services/alertService.ts";
import { rssFeedService } from "../services/rssFeedService.ts";
import QuickReportModal from "./QuickReportModal.tsx";
import VoiceReportModal from "./VoiceReportModal.tsx";
import OpenStreetMap from "./OpenStreetMap.tsx";
import AlertSourceBadge from "./AlertSourceBadge.tsx";
import SourceVerificationModal from "./SourceVerificationModal.tsx";
import DataTransparencyPanel from "./DataTransparencyPanel.tsx";
import type { User } from "../types.ts";

interface CommunityReport {
  id: string;
  farmerId: string;
  farmerName: string;
  village: string;
  crop: string;
  reportType: "pest" | "disease" | "yield" | "weather" | "technique";
  severity: "low" | "medium" | "high" | "critical";
  description: string;
  photo?: string;
  verified: boolean;
  verifiedBy?: string;
  confidence: number;
  corroborations: number;
  actionTaken?: string;
  createdAt: string;
  gpsCoords?: { lat: number; lng: number };
}

interface CommunityDataPanelProps {
  user: User | null;
}

export default function CommunityDataPanel({ user }: CommunityDataPanelProps) {
  const [reports, setReports] = useState<CommunityReport[]>([]);
  const [alerts, setAlerts] = useState<LocalAlert[]>([]);
  const [activeView, setActiveView] = useState<"reports" | "map" | "alerts" | "transparency">("reports");
  const [showQuickReport, setShowQuickReport] = useState(false);
  const [showVoiceReport, setShowVoiceReport] = useState(false);
  const [loading, setLoading] = useState(true);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [corroborations, setCorroborations] = useState<{[key: string]: boolean}>({});
  const [readAlerts, setReadAlerts] = useState<string[]>([]);
  const [selectedAlert, setSelectedAlert] = useState<LocalAlert | null>(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('community_reports')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      if (data && data.length > 0) {
        const formattedReports: CommunityReport[] = data.map(report => ({
          id: report.id,
          farmerId: report.farmer_id,
          farmerName: report.farmer_name,
          village: report.village,
          crop: report.crop,
          reportType: report.report_type,
          severity: report.severity,
          description: report.description,
          photo: report.photo_url,
          verified: report.verified,
          verifiedBy: report.verified_by,
          confidence: report.confidence,
          corroborations: report.corroborations,
          actionTaken: report.action_taken,
          createdAt: report.created_at,
          gpsCoords: report.gps_coords
        }));
        setReports(formattedReports);
      } else {
        // Show sample data if no real reports exist
        const sampleReports: CommunityReport[] = [
          {
            id: "sample-1",
            farmerId: "farmer1",
            farmerName: "Thabo M.",
            village: "Ha Abia, Maseru",
            crop: "Maize",
            reportType: "pest",
            severity: "high",
            description: "Fall armyworm spotted on young maize plants. Affecting 30% of field.",
            verified: true,
            verifiedBy: "Extension Officer Lerato",
            confidence: 95,
            corroborations: 3,
            actionTaken: "Spray with Bt recommended",
            createdAt: "2024-01-20T08:30:00Z"
          }
        ];
        setReports(sampleReports);
      }
      
      // Check user's corroborations if logged in
      if (user) {
        const { data: userCorroborations } = await supabase
          .from('report_corroborations')
          .select('report_id')
          .eq('user_id', user.id);
        
        const corroboratedReports: {[key: string]: boolean} = {};
        userCorroborations?.forEach(c => {
          corroboratedReports[c.report_id] = true;
        });
        setCorroborations(corroboratedReports);
        
        // Get user's read alerts
        const userReadAlerts = alertService.getUserReadAlerts(user.id);
        setReadAlerts(userReadAlerts);
      }
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      // Fallback to sample data on error
      const sampleReports: CommunityReport[] = [
        {
          id: "sample-1",
          farmerId: "farmer1",
          farmerName: "Thabo M.",
          village: "Ha Abia, Maseru",
          crop: "Maize",
          reportType: "pest",
          severity: "high",
          description: "Fall armyworm spotted on young maize plants. Affecting 30% of field.",
          verified: true,
          verifiedBy: "Extension Officer Lerato",
          confidence: 95,
          corroborations: 3,
          actionTaken: "Spray with Bt recommended",
          createdAt: "2024-01-20T08:30:00Z"
        }
      ];
      setReports(sampleReports);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    setAlertsLoading(true);
    try {
      // Fetch government alerts from RSS/web sources (once per day)
      await rssFeedService.fetchIfNeeded();
      
      // Get user's crops from their farms
      let userCrops: string[] = ['maize']; // Default to maize
      if (user) {
        try {
          const { data: farms } = await supabase
            .from('farms')
            .select('crops')
            .eq('user_id', user.id);
          
          if (farms && farms.length > 0) {
            userCrops = farms.flatMap(farm => farm.crops || []);
          }
        } catch (error) {
          console.warn('Failed to fetch user crops, using default:', error);
        }
      }
      
      const localAlerts = await alertService.getLocalAlerts(userCrops);
      setAlerts(localAlerts);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    } finally {
      setAlertsLoading(false);
    }
  };

  useEffect(() => {
    // Test database setup on first load
    const runTests = async () => {
      const tableTest = await testCommunityReportsTable();
      const storageTest = await testStorageBuckets();
      
      console.log('Database setup status:', {
        table: tableTest,
        storage: storageTest
      });
      
      if (!tableTest.exists) {
        console.error('❌ community_reports table missing. Please run the SQL setup.');
      }
      if (!storageTest.photos.exists) {
        console.error('❌ community-photos storage bucket missing.');
      }
      if (!storageTest.audio.exists) {
        console.error('❌ community-audio storage bucket missing.');
      }
    };
    
    runTests();
    fetchReports();
    fetchAlerts();
  }, [user]);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "critical": return "bg-red-100 text-red-800 border-red-200";
      case "high": return "bg-orange-100 text-orange-800 border-orange-200";
      case "medium": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "low": return "bg-green-100 text-green-800 border-green-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getReportIcon = (type: string) => {
    switch (type) {
      case "pest": return <Bug className="w-4 h-4" />;
      case "disease": return <AlertTriangle className="w-4 h-4" />;
      case "yield": return <TrendingUp className="w-4 h-4" />;
      case "weather": return <Cloud className="w-4 h-4" />;
      case "technique": return <Leaf className="w-4 h-4" />;
      default: return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case "weather": return <Cloud className="w-5 h-5" />;
      case "pest": return <Bug className="w-5 h-5" />;
      case "disease": return <AlertTriangle className="w-5 h-5" />;
      case "government": return <Shield className="w-5 h-5" />;
      case "seasonal": return <Clock className="w-5 h-5" />;
      case "community": return <Users className="w-5 h-5" />;
      default: return <AlertTriangle className="w-5 h-5" />;
    }
  };

  const handleMarkAsRead = async (alertId: string) => {
    if (!user) return;
    
    await alertService.markAlertAsRead(alertId, user.id);
    const updatedReadAlerts = alertService.getUserReadAlerts(user.id);
    setReadAlerts(updatedReadAlerts);
  };

  const handleVerifySource = (alert: LocalAlert) => {
    setSelectedAlert(alert);
    setShowVerificationModal(true);
  };

  const formatDistance = (distance?: number) => {
    if (!distance) return "In your area";
    if (distance < 1) return "< 1km away";
    return `${Math.round(distance)}km away`;
  };

  const handleCorroborate = async (reportId: string) => {
    if (!user) return;

    try {
      if (corroborations[reportId]) {
        // Remove corroboration
        await supabase
          .from('report_corroborations')
          .delete()
          .eq('report_id', reportId)
          .eq('user_id', user.id);
        
        // Update report count
        const report = reports.find(r => r.id === reportId);
        if (report) {
          await supabase
            .from('community_reports')
            .update({ corroborations: Math.max(0, report.corroborations - 1) })
            .eq('id', reportId);
        }
        
        setCorroborations(prev => ({ ...prev, [reportId]: false }));
      } else {
        // Add corroboration
        await supabase
          .from('report_corroborations')
          .insert({ report_id: reportId, user_id: user.id });
        
        // Update report count
        const report = reports.find(r => r.id === reportId);
        if (report) {
          await supabase
            .from('community_reports')
            .update({ corroborations: report.corroborations + 1 })
            .eq('id', reportId);
        }
        
        setCorroborations(prev => ({ ...prev, [reportId]: true }));
      }
      
      // Refresh reports to show updated counts
      fetchReports();
    } catch (error) {
      console.error('Corroboration failed:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Users className="w-8 h-8 text-green-600" />
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Community Reports</h2>
          <p className="text-gray-600">Verified field reports from Lesotho farmers</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <Camera className="w-8 h-8 text-green-600 mb-2" />
          <h4 className="font-semibold mb-2">Quick Report</h4>
          <p className="text-sm text-gray-600 mb-3">Report pest, disease, or crop observations</p>
          <button
            onClick={() => setShowQuickReport(true)}
            className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
          >
            Create Report
          </button>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <Mic className="w-8 h-8 text-blue-600 mb-2" />
          <h4 className="font-semibold mb-2">Voice Report</h4>
          <p className="text-sm text-gray-600 mb-3">Record observations in Sesotho or English</p>
          <button
            onClick={() => setShowVoiceReport(true)}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Record Voice
          </button>
        </div>

        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
          <MapPin className="w-8 h-8 text-orange-600 mb-2" />
          <h4 className="font-semibold mb-2">Local Alerts</h4>
          <p className="text-sm text-gray-600 mb-3">View verified reports near your location</p>
          <button
            onClick={() => setActiveView("alerts")}
            className="w-full px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors"
          >
            View Alerts
          </button>
        </div>
      </div>

      {/* Fixed View Toggle */}
      <div className="bg-white rounded-lg shadow-sm border border-green-200 flex flex-col h-[600px]">
        <div className="flex border-b border-green-200 sticky top-0 bg-white z-10">
          {[
            { id: "reports", label: "Recent Reports", icon: MessageSquare },
            { id: "alerts", label: "Active Alerts", icon: AlertTriangle },
            { id: "transparency", label: "Data Sources", icon: Shield },
            { id: "map", label: "Map View", icon: MapPin }
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveView(id as any)}
              className={`flex items-center space-x-2 px-6 py-4 font-medium transition-colors ${
                activeView === id
                  ? "bg-green-50 text-green-700 border-b-2 border-green-600"
                  : "text-gray-600 hover:text-gray-800 hover:bg-green-50"
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-hidden">
          {activeView === "reports" && (
            <div className="p-6 h-full overflow-y-auto pb-32">
              <div className="space-y-4">
                {reports.map((report) => (
                <div key={report.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        {getReportIcon(report.reportType)}
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-800">{report.farmerName}</span>
                          {report.verified && (
                            <div className="flex items-center space-x-1">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span className="text-xs text-green-600">Verified</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                          <MapPin className="w-3 h-3" />
                          <span>{report.village}</span>
                          <span>•</span>
                          <span>{report.crop}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${getSeverityColor(report.severity)}`}>
                        {report.severity.toUpperCase()}
                      </span>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-3">{report.description}</p>

                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <Shield className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">{report.confidence}% confidence</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">{report.corroborations} confirmations</span>
                      </div>
                      {user && (
                        <button
                          onClick={() => handleCorroborate(report.id)}
                          className={`flex items-center space-x-1 px-2 py-1 rounded text-xs transition-colors ${
                            corroborations[report.id]
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <ThumbsUp className="w-3 h-3" />
                          <span>{corroborations[report.id] ? 'Confirmed' : 'Confirm'}</span>
                        </button>
                      )}
                    </div>
                    <div className="flex items-center space-x-1 text-gray-500">
                      <Clock className="w-3 h-3" />
                      <span>{new Date(report.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {report.verified && report.actionTaken && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded">
                      <div className="flex items-center space-x-2 mb-1">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-800">Recommended Action</span>
                      </div>
                      <p className="text-sm text-green-700">{report.actionTaken}</p>
                      {report.verifiedBy && (
                        <p className="text-xs text-green-600 mt-1">Verified by {report.verifiedBy}</p>
                      )}
                    </div>
                  )}
                </div>
                ))}
              </div>
            </div>
          )}

          {activeView === "alerts" && (
            <div className="p-6 h-full overflow-y-auto pb-32">
              {/* Alert Verification Panel */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-blue-800">Alert Source Verification</h4>
                    <p className="text-sm text-blue-600">Check if alerts are from real government sources</p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={async () => {
                        const { alertVerificationService } = await import('../utils/alertVerification.ts');
                        await alertVerificationService.forceRealAlertsFetch();
                        fetchAlerts(); // Refresh alerts
                        alert('Attempted to fetch real government alerts. Check console for results.');
                      }}
                      className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                    >
                      Fetch Real Alerts
                    </button>
                    <button
                      onClick={async () => {
                        const { alertVerificationService } = await import('../utils/alertVerification.ts');
                        const report = await alertVerificationService.generateVerificationReport();
                        console.log(report);
                        alert('Verification report logged to console. Check browser console for details.');
                      }}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                    >
                      Verify Sources
                    </button>
                  </div>
                </div>
              </div>
              
              {alertsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                </div>
              ) : (
                <div className="space-y-4">
                  {alerts.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p>No active alerts in your area</p>
                      <p className="text-sm">We'll notify you when new alerts are available</p>
                    </div>
                  ) : (
                    alerts.map((alert) => {
                      const isRead = readAlerts.includes(alert.id);
                      return (
                        <div key={alert.id} className={`border rounded-lg p-4 transition-all ${
                          isRead ? 'bg-gray-50 border-gray-200 opacity-75' : 
                          alert.severity === 'critical' ? 'bg-red-50 border-red-200' :
                          alert.severity === 'high' ? 'bg-orange-50 border-orange-200' :
                          alert.severity === 'medium' ? 'bg-yellow-50 border-yellow-200' :
                          'bg-green-50 border-green-200'
                        }`}>
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <div className={`p-2 rounded-full ${
                                alert.severity === 'critical' ? 'bg-red-100 text-red-600' :
                                alert.severity === 'high' ? 'bg-orange-100 text-orange-600' :
                                alert.severity === 'medium' ? 'bg-yellow-100 text-yellow-600' :
                                'bg-green-100 text-green-600'
                              }`}>
                                {getAlertIcon(alert.alert_type)}
                              </div>
                              <div>
                                <h3 className={`font-semibold ${
                                  alert.severity === 'critical' ? 'text-red-800' :
                                  alert.severity === 'high' ? 'text-orange-800' :
                                  alert.severity === 'medium' ? 'text-yellow-800' :
                                  'text-green-800'
                                }`}>{alert.title}</h3>
                                <div className="flex items-center space-x-2 text-sm text-gray-600">
                                  <Navigation className="w-3 h-3" />
                                  <span>{formatDistance(alert.distance)}</span>
                                  <span>•</span>
                                  <span className={`px-2 py-1 rounded text-xs font-medium border ${getSeverityColor(alert.severity)}`}>
                                    {alert.severity.toUpperCase()}
                                  </span>
                                </div>
                              </div>
                            </div>
                            {user && (
                              <button
                                onClick={() => handleMarkAsRead(alert.id)}
                                className={`p-1 rounded transition-colors ${
                                  isRead ? 'text-gray-400' : 'text-gray-600 hover:text-gray-800'
                                }`}
                                title={isRead ? 'Mark as unread' : 'Mark as read'}
                              >
                                {isRead ? <Eye className="w-4 h-4" /> : <X className="w-4 h-4" />}
                              </button>
                            )}
                          </div>

                          <p className={`text-sm mb-3 ${
                            alert.severity === 'critical' ? 'text-red-700' :
                            alert.severity === 'high' ? 'text-orange-700' :
                            alert.severity === 'medium' ? 'text-yellow-700' :
                            'text-green-700'
                          }`}>{alert.description}</p>

                          {alert.crops_affected && alert.crops_affected.length > 0 && (
                            <div className="flex items-center space-x-2 mb-2">
                              <Leaf className="w-4 h-4 text-gray-500" />
                              <span className="text-sm text-gray-600">
                                Affects: {alert.crops_affected.join(', ')}
                              </span>
                            </div>
                          )}

                          {alert.action_required && (
                            <div className={`mt-3 p-3 rounded border ${
                              alert.severity === 'critical' ? 'bg-red-100 border-red-200' :
                              alert.severity === 'high' ? 'bg-orange-100 border-orange-200' :
                              alert.severity === 'medium' ? 'bg-yellow-100 border-yellow-200' :
                              'bg-green-100 border-green-200'
                            }`}>
                              <div className="flex items-center space-x-2 mb-1">
                                <CheckCircle className={`w-4 h-4 ${
                                  alert.severity === 'critical' ? 'text-red-600' :
                                  alert.severity === 'high' ? 'text-orange-600' :
                                  alert.severity === 'medium' ? 'text-yellow-600' :
                                  'text-green-600'
                                }`} />
                                <span className={`text-sm font-medium ${
                                  alert.severity === 'critical' ? 'text-red-800' :
                                  alert.severity === 'high' ? 'text-orange-800' :
                                  alert.severity === 'medium' ? 'text-yellow-800' :
                                  'text-green-800'
                                }`}>Recommended Action</span>
                              </div>
                              <p className={`text-sm ${
                                alert.severity === 'critical' ? 'text-red-700' :
                                alert.severity === 'high' ? 'text-orange-700' :
                                alert.severity === 'medium' ? 'text-yellow-700' :
                                'text-green-700'
                              }`}>{alert.action_required}</p>
                            </div>
                          )}

                          <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
                            <div className="flex items-center space-x-2">
                              <span>Source: {alert.source_type.replace('_', ' ')}</span>
                              <AlertSourceBadge sourceType={alert.source_type} />
                              <button
                                onClick={() => handleVerifySource(alert)}
                                className="flex items-center space-x-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 transition-colors"
                                title="Verify source authenticity"
                              >
                                <Info className="w-3 h-3" />
                                <span>Verify</span>
                              </button>
                            </div>
                            <span>{new Date(alert.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          )}

          {activeView === "transparency" && (
            <div className="p-6 h-full overflow-y-auto pb-32">
              <DataTransparencyPanel />
            </div>
          )}

          {activeView === "map" && (
            <div className="h-full">
              <OpenStreetMap reports={reports} />
            </div>
          )}
        </div>
      </div>
      {/* Modals */}
      {user && (
        <>
          <QuickReportModal
            open={showQuickReport}
            onClose={() => setShowQuickReport(false)}
            user={user}
            onReportCreated={() => {
              fetchReports();
              fetchAlerts();
            }}
          />
          <VoiceReportModal
            open={showVoiceReport}
            onClose={() => setShowVoiceReport(false)}
            user={user}
            onReportCreated={() => {
              fetchReports();
              fetchAlerts();
            }}
          />
        </>
      )}
      
      {/* Source Verification Modal */}
      {selectedAlert && (
        <SourceVerificationModal
          open={showVerificationModal}
          onClose={() => {
            setShowVerificationModal(false);
            setSelectedAlert(null);
          }}
          alert={selectedAlert}
        />
      )}
    </div>
  );
}